import React from "react";
import { X } from "lucide-react";

// Phase 4a-sweep — region-aware pay breakdown.
//   * India  → "CTC Breakdown": earnings → statutory deductions → net,
//              plus employer contributions (PF/ESIC/gratuity…) and the
//              Total CTC line + formula. (India concept — unchanged.)
//   * UK     → "Salary Breakdown": Gross → deductions (PAYE/NI/pension/
//              student loan) → Net. NO CTC, NO employer-contribution
//              section, NO CTC formula (UK payroll has no CTC).
//   * unknown→ neutral "Pay Breakdown": Gross → deductions → Net, no CTC.
// Display-only: no numbers/calculations change — this only decides which
// labels/sections render for the employee's region.

const STATUTORY_LABELS = {
  pf_employer:      "PF Employer (12%)",
  esic_employer:    "ESIC Employer (3.25%)",
  lwf_employer:     "Labour Welfare Fund (Employer)",
  gratuity:         "Gratuity Provision (4.81%)",
  nps_employer:     "NPS Employer Contribution",
  pf_employee:      "PF Employee (12%)",
  esic_employee:    "ESIC Employee (0.75%)",
  lwf_employee:     "Labour Welfare Fund (Employee)",
  professional_tax: "Professional Tax",
  tds:              "TDS (Tax Deducted at Source)",
  income_tax:       "Income Tax",
  nps_employee:     "NPS Employee Contribution",
};

// UK deduction labels (applied when region !== India).
const UK_LABELS = {
  paye:               "PAYE Tax",
  paye_tax:           "PAYE Tax",
  tax:                "PAYE Tax",
  income_tax:         "PAYE Tax",
  ni:                 "National Insurance",
  ee_ni:              "National Insurance",
  employee_ni:        "National Insurance",
  national_insurance: "National Insurance",
  pension:            "Pension",
  ee_pension:         "Pension",
  employee_pension:   "Pension",
  student_loan:       "Student Loan",
  studentloan:        "Student Loan",
};

function money(n) {
  if (n == null || n === 0) return "—";
  return typeof n === "number"
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(n);
}

function Row({ label, monthly, annual, highlight }) {
  return (
    <tr className={`border-b border-white/5 ${highlight ? "bg-white/3" : ""}`}>
      <td className="px-4 py-2.5 text-sm text-white/70">{label}</td>
      <td className="px-4 py-2.5 text-sm text-white/60 text-right">{money(monthly)}</td>
      <td className="px-4 py-2.5 text-sm text-white/80 text-right font-medium">{money(annual)}</td>
    </tr>
  );
}

export default function CTCPopup({ visible, payslips, selectedYear, onClose, region = "India" }) {
  if (!visible) return null;

  const isIndia = region === "India";
  const title   = isIndia ? "CTC Breakdown" : region === "UK" ? "Salary Breakdown" : "Pay Breakdown";
  const dednLabel = (k) => (isIndia ? (STATUTORY_LABELS[k] || k) : (UK_LABELS[k] || STATUTORY_LABELS[k] || k));

  const filtered = (payslips || []).filter(p => p.financialYear === selectedYear);
  const months   = Math.max(1, filtered.length);

  // Aggregate all fields across months
  const aggregate = filtered.reduce((acc, p) => {
    const add = (section) => Object.entries(p[section] || {}).forEach(([k, v]) => {
      acc[section] = acc[section] || {};
      acc[section][k] = (acc[section][k] || 0) + (Number(v) || 0);
    });
    add("earnings"); add("deductions"); add("statutory");
    return acc;
  }, {});

  const earnings    = aggregate.earnings   || {};
  const deductions  = aggregate.deductions || {};
  const statutory   = aggregate.statutory  || {};

  const grossAnnual  = Object.values(earnings).reduce((a, b) => a + b, 0);

  // Employer contrib (India only — the CTC adders)
  const employerKeys = ["pf_employer", "esic_employer", "lwf_employer", "gratuity", "nps_employer"];
  const employerAnnual = employerKeys.reduce((a, k) => a + (statutory[k] || 0), 0);

  const employeeStatKeys = ["pf_employee", "esic_employee", "professional_tax", "lwf_employee", "tds", "income_tax", "nps_employee"];

  // Build the deduction rows shown to the employee.
  //   India: the statutory-employee keys (from statutory/deductions) +
  //          any custom deductions.
  //   UK/neutral: whatever the payslip's `deductions` carries (PAYE/NI/…),
  //          relabelled to UK terms — no India statutory scaffolding.
  let deductionRows;   // [label, annual]
  if (isIndia) {
    const statRows = employeeStatKeys
      .map(k => [dednLabel(k), statutory[k] || deductions[k] || 0])
      .filter(([, v]) => v);
    const customRows = Object.entries(deductions)
      .filter(([k]) => !employeeStatKeys.includes(k))
      .map(([k, v]) => [k, v]);
    deductionRows = [...statRows, ...customRows];
  } else {
    deductionRows = Object.entries(deductions).map(([k, v]) => [dednLabel(k), v]);
  }

  const totalDeductAnnual = deductionRows.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  const netAnnual         = grossAnnual - totalDeductAnnual;
  const ctcAnnual         = grossAnnual + employerAnnual;

  const summaryTiles = [
    { label: "Gross Annual",   value: money(grossAnnual), color: "#00ff90" },
    { label: "Net Annual Pay", value: money(netAnnual),   color: "#60a5fa" },
    ...(isIndia ? [{ label: "Total CTC", value: money(ctcAnnual), color: "#c084fc" }] : []),
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="w-full max-w-3xl rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022] flex flex-col max-h-[90vh]">

        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-white">{title} · {selectedYear}</h2>
            <p className="text-xs text-[#00ff90]/50 mt-0.5">Based on {months} payslip{months !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">

          {/* Summary row */}
          <div className={`grid gap-3 ${isIndia ? "grid-cols-3" : "grid-cols-2"}`}>
            {summaryTiles.map(({ label, value, color }) => (
              <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
                <p className="text-xl font-bold" style={{ color }}>{value}</p>
                <p className="text-xs text-white/30 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Main table */}
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-white/3">
                  <th className="px-4 py-3 text-left text-xs text-[#00ff90]/50 uppercase tracking-wider">Component</th>
                  <th className="px-4 py-3 text-right text-xs text-[#00ff90]/50 uppercase tracking-wider">Monthly</th>
                  <th className="px-4 py-3 text-right text-xs text-[#00ff90]/50 uppercase tracking-wider">Annual</th>
                </tr>
              </thead>
              <tbody>
                {/* Earnings */}
                <tr className="bg-[#00ff90]/5">
                  <td colSpan={3} className="px-4 py-2 text-xs text-[#00ff90]/60 uppercase tracking-wider font-semibold">Earnings</td>
                </tr>
                {Object.entries(earnings).map(([k, annual]) => (
                  <Row key={k} label={k} monthly={annual / months} annual={annual} />
                ))}
                <tr className="bg-[#00ff90]/8 font-semibold">
                  <td className="px-4 py-2.5 text-sm text-[#00ff90]">Gross Salary</td>
                  <td className="px-4 py-2.5 text-sm text-[#00ff90] text-right">{money(grossAnnual / months)}</td>
                  <td className="px-4 py-2.5 text-sm text-[#00ff90] text-right">{money(grossAnnual)}</td>
                </tr>

                {/* Deductions */}
                <tr className="bg-yellow-500/5">
                  <td colSpan={3} className="px-4 py-2 text-xs text-yellow-400/60 uppercase tracking-wider font-semibold">
                    {isIndia ? "Statutory Deductions (Employee)" : "Deductions"}
                  </td>
                </tr>
                {deductionRows.map(([label, annual]) => (
                  <Row key={label} label={label} monthly={annual / months} annual={annual} />
                ))}

                <tr className="bg-red-500/8 font-semibold">
                  <td className="px-4 py-2.5 text-sm text-red-400">Total Deductions</td>
                  <td className="px-4 py-2.5 text-sm text-red-400 text-right">{money(totalDeductAnnual / months)}</td>
                  <td className="px-4 py-2.5 text-sm text-red-400 text-right">{money(totalDeductAnnual)}</td>
                </tr>

                {/* Net pay */}
                <tr className="bg-blue-500/8 font-bold">
                  <td className="px-4 py-2.5 text-sm text-blue-400">Net Pay</td>
                  <td className="px-4 py-2.5 text-sm text-blue-400 text-right">{money(netAnnual / months)}</td>
                  <td className="px-4 py-2.5 text-sm text-blue-400 text-right">{money(netAnnual)}</td>
                </tr>

                {/* Employer contributions + CTC total — INDIA ONLY */}
                {isIndia && (
                  <>
                    <tr className="bg-purple-500/5">
                      <td colSpan={3} className="px-4 py-2 text-xs text-purple-400/60 uppercase tracking-wider font-semibold">Employer Contributions (Added to CTC)</td>
                    </tr>
                    {employerKeys.map(k => {
                      const annual = statutory[k] || 0;
                      if (!annual) return null;
                      return <Row key={k} label={STATUTORY_LABELS[k] || k} monthly={annual / months} annual={annual} />;
                    })}
                    <tr className="bg-purple-500/10 font-bold">
                      <td className="px-4 py-2.5 text-sm text-purple-400">Total CTC</td>
                      <td className="px-4 py-2.5 text-sm text-purple-400 text-right">{money(ctcAnnual / months)}</td>
                      <td className="px-4 py-2.5 text-sm text-purple-400 text-right">{money(ctcAnnual)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {isIndia ? (
            <p className="text-xs text-white/20 text-center">
              CTC = Gross Salary + Employer PF + Employer ESIC + Gratuity + other employer contributions
            </p>
          ) : (
            <p className="text-xs text-white/20 text-center">
              Net Pay = Gross Salary − deductions (PAYE tax, National Insurance, pension, student loan)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
